import React, { useEffect, useState } from 'react';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'react-toastify/dist/ReactToastify.css';
import Stack from '@mui/material/Stack';
import { CssBaseline, ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAppDispatch, useAppSelector } from './store';
import {
  selectAccessToken,
  selectCustomLocalizations,
  selectFrontendParams,
  selectUser,
} from './store/general/general-slice-selectors';
import { getToken } from './services/data/axios-base';
import { localLogin, setIsAdvancedModeEnabled } from './store/general/general-slice';
import { customTheme } from './utils/theme';
import { Bounce, ToastContainer } from 'react-toastify';
import {
  getCustomLocalizations,
  getFrontendParams,
  getUserByToken,
} from './store/general/general-actions';
import PageNotFound from './components/pages/PageNotFound';
import LoginPage from './components/pages/LoginPage';
import { navigationOpenStorageName } from './components/ui/MainNavigationBar';
import CentralContentBlock from './components/ui/CentralContentBlock';
import TopBar from './components/ui/TopBar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CookieConsentBox from './components/ui/CookieConsentBox';
import StartupErrorScreen from './components/ui/StartupErrorScreen';
import AgreementPopups from './components/agreements/AgreementPopups';
import AppUpdateDialog from './components/ui/AppUpdateDialog';
import OrganizationInitPopup from './components/organizations/OrganizationInitPopup';
import RegisterPage from './components/pages/RegisterPage';
import UnsubscribePage from './components/pages/UnsubscribePage';
import { selectOrganizationUsersData } from './store/organization/organization-slice-selectors';
import { useCookieConsent } from './hooks/useCookieConsent';
import { ConsentDecision } from 'utils/cookieConsent';
import NotificationHandler from './components/notifications/NotificationHandler';
import { getCurrentLanguage, STORED_LANG_KEY } from './i18n';
import i18next from 'i18next';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectIsReadOnly } from '@common/store/app-configurations/app-configurations-selectors';
import OrganizationDashboard from 'components/organizations/OrganizationDashboard';
import OrganizationDataDashboard from 'components/organizations/OrganizationDataDashboard';
import MissionHistoryPage from 'components/missions/MissionHistoryPage';
import UsersPage from 'components/organizations/organization-pages/UsersPage';
import OrganizationsPage from 'components/organizations/organization-pages/OrganizationsPage';
import OrganizationSettingsPage from 'components/organizations/organization-pages/OrganizationSettingsPage';
import AdministrationPage from 'components/organizations/organization-pages/AdministrationPage';
import NavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import FleetDashboard from 'components/fleet-management/FleetDashboard';
import LegalPage from 'components/legal/LegalPage';
import AccountDataPage from 'components/public/AccountDataPage';
import PublicLayout from 'components/public/PublicLayout';
import {
  disableTracking,
  enableTracking,
  identifyUser,
  initAnalytics,
  isInitialized,
} from 'services/analytics';
import { usePageViewTracking } from 'hooks/usePageViewTracking';

export const getRouteBase = () => '/';

const App: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const theme = useTheme();
  const matchesSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const user = useAppSelector((state) => selectUser(state));
  const token = useAppSelector((state) => selectAccessToken(state));
  const customLocalizations = useAppSelector((state) => selectCustomLocalizations(state));
  const organizationUsersData = useAppSelector((state) => selectOrganizationUsersData(state));
  const frontendParams = useAppSelector((state) => selectFrontendParams(state));

  const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));

  const { consent } = useCookieConsent();

  const [isNavOpen, setIsNavOpen] = useState(
    localStorage.getItem(navigationOpenStorageName) === 'true',
  );
  const [hasStartupFailed, setHasStartupFailed] = useState(false);
  const isLoggedIn = (user.data?.id ?? '').length > 0;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    SplashScreen.hide();

    const backButtonListener = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        navigate(-1);
      } else {
        CapacitorApp.exitApp();
      }
    });

    return () => {
      backButtonListener.then((listener) => listener.remove());
    };
  }, [navigate]);

  useEffect(() => {
    const hostname = window.location.hostname;
    const title = document.title;
    if (hostname.includes('beta')) {
      document.title = 'Beta ' + title;
    } else if (hostname !== 'sphere.kemaro.ch') {
      document.title = 'Dev ' + title;
    }
  }, []);

  useEffect(() => {
    if (!token.localStorageChecked) {
      const accessToken = getToken();
      if (accessToken !== null) {
        dispatch(localLogin(accessToken));
        dispatch(getUserByToken());
      } else {
        dispatch(localLogin(null));
      }
    }
  }, [dispatch, token.localStorageChecked]);

  useEffect(() => {
    if (user.data.language.toString() !== getCurrentLanguage().languageCode) {
      i18next.changeLanguage(user.data.language, (err) => {
        if (err) {
          return console.log('language could not be changed', err);
        } else {
          localStorage.setItem(STORED_LANG_KEY, user.data.language);
        }
      });
    }
  }, [user.data.language]);

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'a' && !isReadOnly) {
        dispatch(setIsAdvancedModeEnabled(true));
        event.preventDefault();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [dispatch, isReadOnly]);

  useEffect(() => {
    if (frontendParams.getStatus === HttpFetchStatus.Idle) {
      dispatch(getFrontendParams());
    }
  }, [dispatch, frontendParams.getStatus]);

  useEffect(() => {
    if (frontendParams.getStatus === HttpFetchStatus.Failed) {
      setHasStartupFailed(true);
    } else if (frontendParams.getStatus === HttpFetchStatus.Success) {
      setHasStartupFailed(false);
    }
  }, [frontendParams.getStatus]);

  const isAuthSettled =
    token.localStorageChecked &&
    (token.token == null || isLoggedIn || user.rest.getStatus === HttpFetchStatus.Failed);
  const amplitudeApiKey = frontendParams.data?.amplitudeApiKey;
  const isAnalyticsActive =
    consent === ConsentDecision.GRANTED &&
    frontendParams.getStatus === HttpFetchStatus.Success &&
    amplitudeApiKey != null &&
    isAuthSettled;

  useEffect(() => {
    if (isAnalyticsActive && amplitudeApiKey != null) {
      if (!isInitialized()) {
        initAnalytics(amplitudeApiKey, isLoggedIn ? user.data.id : undefined);
      }
      enableTracking();
      if (isLoggedIn) {
        identifyUser({
          userId: user.data.id,
          userRole: user.data.role,
          organizationId: user.data.organization.id,
          organizationRole: user.data.organization.role,
          organizationRobotCount: user.data.organization.robotCount,
          language: user.data.language,
          loginVariant: user.data.lastLoginVariant ?? null,
          isTwoFactorEnabled: user.data.isTwoFactorEnabled,
          accountCreatedAt: user.data.dateCreated,
        });
      }
    } else if (consent === ConsentDecision.DENIED && isInitialized()) {
      disableTracking();
    }
    // consent === null, or denied-but-never-initialised → do nothing; tracking never starts.
  }, [consent, isAnalyticsActive, amplitudeApiKey, isLoggedIn, user.data]);

  usePageViewTracking(isLoggedIn, isAnalyticsActive);

  useEffect(() => {
    if (customLocalizations.rest.getStatus === HttpFetchStatus.Idle && isLoggedIn) {
      dispatch(getCustomLocalizations());
    }
  }, [customLocalizations.rest.getStatus, dispatch, isLoggedIn]);

  useEffect(() => {
    if (organizationUsersData.rest.patchStatus === HttpFetchStatus.Success) {
      dispatch(getUserByToken());
    }
  }, [dispatch, organizationUsersData.rest.patchStatus]);

  const navBarChangeHandler = () => {
    setIsNavOpen((prevState) => {
      localStorage.setItem(navigationOpenStorageName, (!prevState).toString());
      return !prevState;
    });
  };

  const isFleetManagementAppOpen = pathname.startsWith(NavigationRoutes.Insights);

  useEffect(() => {
    if (!isFleetManagementAppOpen) {
      setIsNavOpen(localStorage.getItem(navigationOpenStorageName) === 'true');
    }
  }, [isFleetManagementAppOpen]);

  // The logged-out UI cannot render without the Google client id from the frontend
  // params. If that request never succeeds there is nothing to show, so render a retry
  // screen rather than an empty page -- on the Android app the empty page is
  // indistinguishable from a crash.
  const isStartupBlocked =
    hasStartupFailed ||
    (frontendParams.getStatus === HttpFetchStatus.Success &&
      frontendParams.data?.googleClientId == null);

  const retryFrontendParamsHandler = () => {
    dispatch(getFrontendParams());
  };

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <ToastContainer
        position={matchesSmUp ? 'bottom-left' : 'top-center'}
        newestOnTop={false}
        pauseOnFocusLoss
        pauseOnHover
        hideProgressBar={false}
        draggable
        autoClose={2000}
        style={{
          width: 'max-content',
          marginLeft: matchesSmUp ? undefined : '50%',
          transform: matchesSmUp ? undefined : 'translateX(-50%)',
          marginTop: matchesSmUp ? undefined : theme.spacing(2),
          maxWidth: `calc(100vw - ${theme.spacing(4)})`,
        }}
        bodyStyle={{ width: undefined }}
        transition={Bounce}
      />
      <AppUpdateDialog>
        {isLoggedIn ? (
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              height: '100%',
              maxWidth: '100vw',
              overflow: 'hidden',
              position: 'relative',
            })}
          >
            <CookieConsentBox />
            <AgreementPopups />
            <OrganizationInitPopup />
            <NotificationHandler />

            {isFleetManagementAppOpen ? (
              <Routes>
                <Route path={NavigationRoutes.Insights} element={<FleetDashboard />} />
                <Route
                  path={NavigationRoutes.InsightsDatasets}
                  element={<FleetDashboard datasetPage />}
                />
              </Routes>
            ) : (
              <React.Fragment>
                <TopBar isNavOpen={isNavOpen} onNavChangeHandler={navBarChangeHandler} />
                <CentralContentBlock onNavChangeHandler={navBarChangeHandler} isNavOpen={isNavOpen}>
                  <Routes>
                    <Route path={NavigationRoutes.Dashboard} element={<OrganizationDashboard />} />
                    <Route path={`${NavigationRoutes.Register}`} element={<RegisterPage />} />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId`}
                      element={<OrganizationDashboard />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Statistics}`}
                      element={<OrganizationDataDashboard />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.MissionHistory}`}
                      element={<MissionHistoryPage dashboardPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Users}`}
                      element={<UsersPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Organizations}`}
                      element={<OrganizationsPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Settings}`}
                      element={<OrganizationSettingsPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Administration}`}
                      element={<AdministrationPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Legal}`}
                      element={<LegalPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.AccountData}`}
                      element={<AccountDataPage />}
                    />
                    <Route
                      path={`${NavigationRoutes.Dashboard}:orgId${NavigationRoutes.Unsubscribe}`}
                      element={<UnsubscribePage />}
                    />
                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </CentralContentBlock>
              </React.Fragment>
            )}
          </Stack>
        ) : frontendParams.data?.googleClientId != null ? (
          <Routes>
            <Route
              path={`${NavigationRoutes.Legal}`}
              element={
                <PublicLayout>
                  <LegalPage />
                </PublicLayout>
              }
            />
            <Route
              path={`${NavigationRoutes.AccountData}`}
              element={
                <PublicLayout>
                  <AccountDataPage />
                </PublicLayout>
              }
            />
            <Route path={`${NavigationRoutes.Register}`} element={<RegisterPage />} />
            <Route path={`${NavigationRoutes.Unsubscribe}`} element={<UnsubscribePage />} />
            <Route path="*" element={<LoginPage />} />
          </Routes>
        ) : isStartupBlocked ? (
          <StartupErrorScreen
            onRetry={retryFrontendParamsHandler}
            isRetrying={frontendParams.getStatus === HttpFetchStatus.Loading}
          />
        ) : null}
      </AppUpdateDialog>
    </ThemeProvider>
  );
};

export default App;
